import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLateCancelCounts } from 'src/modules/cancellation-policy/hooks'
import { CalendarList } from 'src/modules/calendar/components'
import { FormattedText, Icon, Typography } from 'src/modules/core/components'
import { SidebarBrand } from 'src/modules/core/components/sidebar-brand'
import { navigation } from 'src/modules/core/constants/navigation'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { useSidebar } from 'src/modules/core/context/sidebar.context'
import type { Navigation } from 'src/modules/core/types/navigation.types'
import { DashboardCallout } from 'src/modules/dashboard-callout/dashboard-callout'
import classes from './sidebar.module.scss'

/** The nav list is static; the late cancels item depends on the account's counts. */
function useVisibleNavigation(): Navigation[] {
  const { data: counts } = useLateCancelCounts()

  return useMemo(
    () =>
      navigation
        .filter((item) => item.path !== routeNames.lateCancels.path || counts?.showNav === true)
        .map((item) => (item.path === routeNames.lateCancels.path ? { ...item, badge: counts?.open } : item)),
    [counts],
  )
}

function NavBadge({ item, isExpanded }: { item: Navigation; isExpanded: boolean }) {
  if (!item.badge) return null

  return (
    <span
      className={classNames(classes.badge, { [classes.badgeCollapsed]: !isExpanded })}
      aria-label={`${item.badge} open ${item.text.toLowerCase()}`}
      data-testid="sidebar-nav-badge"
    >
      {item.badge}
    </span>
  )
}

export function Sidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isCollapsed, setIsCollapsed } = useSidebar()
  const [isHovered, setIsHovered] = useState(false)

  const handleCollapseToggle = useCallback(() => {
    setIsCollapsed((prev) => !prev)
  }, [setIsCollapsed])

  const handleMouseEnter = useCallback(() => {
    if (isCollapsed) {
      setIsHovered(true)
    }
  }, [isCollapsed])

  const handleMouseLeave = useCallback(() => {
    if (isCollapsed) {
      setIsHovered(false)
    }
  }, [isCollapsed])

  const isExpanded = !isCollapsed || isHovered
  const items = useVisibleNavigation()

  return (
    <div
      className={classNames(classes.root, {
        [classes.collapsed]: isCollapsed && !isHovered,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={classes.header}>
        <SidebarBrand titleClassName={classes.title} lockupClassName={classes.lockup} />
        <div className={classes.headerActions}>
          <button
            className={classNames(classes.collapseButton, {
              [classes.rotated]: isCollapsed,
            })}
            onClick={handleCollapseToggle}
          >
            <Icon name="arrowLeft" className={classes.collapseIcon} />
          </button>
        </div>
      </div>

      <ul className={classes.sidebar}>
        {items.map((item) => (
          <li
            key={item.path}
            className={classNames(classes.sidebarItem, {
              [classes.active]: location.pathname === item.path,
            })}
            onClick={() => navigate(item.path)}
          >
            <Icon name={item.icon} className={classes.icon} />
            {isExpanded && (
              <Typography variant="body" className={classes.text}>
                {item.text}
              </Typography>
            )}
            <NavBadge item={item} isExpanded={isExpanded} />
          </li>
        ))}
      </ul>
      {location.pathname === routeNames.dashboard.path && isExpanded && (
        <>
          <div className={classNames(classes.lineWrapper, classes.line)} />
          <CalendarList
            listClassName={classes.calendarList}
            withUpgradeButton
            title={
              <Typography variant="h4" className={classes.secondaryTitle}>
                <FormattedText id="calendars.dashboard.title" />
              </Typography>
            }
          >
            <div className={classes.line} />
          </CalendarList>
        </>
      )}
      {isExpanded && <DashboardCallout />}
    </div>
  )
}
